import React from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/cards';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { openExternalUrl } from '@/utils';


interface PersonalEmailProvider {
  id: string;
  label: string;
  url: string;
}

const PERSONAL_EMAIL_PROVIDERS: readonly PersonalEmailProvider[] = [
  { id: 'gmail', label: 'Gmail', url: 'https://accounts.google.com/signup' },
  { id: 'outlook', label: 'Outlook', url: 'https://signup.live.com/signup' },
  { id: 'yahoo', label: 'Yahoo', url: 'https://login.yahoo.com/account/create' },
];

interface PersonalEmailProvidersCardProps {
  className?: string;
}

/**
 * Accesos directos a la creación de una cuenta de correo personal (Gmail, Outlook, Yahoo).
 * Se muestra en el paso Contacto de la creación de colaborador y en la pestaña Contacto
 * del perfil. Cada acceso abre una pestaña nueva sin alterar el formulario actual.
 */
export const PersonalEmailProvidersCard: React.FC<PersonalEmailProvidersCardProps> = ({
  className = '',
}) => {
  const { t: translate } = useTranslation();

  return (
    <Card
      elevation={1}
      borderRadius="12px"
      padding="16px"
      className={`w-full max-w-[468px] ${className}`}
      data-testid="personal-email-providers-card"
    >
      <div className="flex items-start gap-3">
        <IconScout
          name="envelope"
          size={20}
          color={colors.primary.dash}
          className="mt-1 shrink-0"
        />
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Text variant="subheader-medium" color={colors.grays.neutral33}>
              {translate('employee:contactForm.noPersonalEmail.title')}
            </Text>
            <Text variant="small-regular" color={colors.grays.neutral66}>
              {translate('employee:contactForm.noPersonalEmail.description')}
            </Text>
          </div>
          <div className="flex flex-wrap gap-2">
            {PERSONAL_EMAIL_PROVIDERS.map((provider) => (
              <PrimaryButton
                key={provider.id}
                type="button"
                variant="outline"
                iconLeft={<IconScout name="externalLink" size={20} />}
                iconSize={20}
                onClick={() => openExternalUrl(provider.url)}
                data-testid={`personal-email-provider-${provider.id}`}
              >
                {provider.label}
              </PrimaryButton>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PersonalEmailProvidersCard;
